import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {capitalize} from 'util/NameUtil';
import {ElementKeyboardEvent, InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import {
  BoxContainersLookupStepSpec,
  FillDirection,
  FormErrors,
  GridFilterParams,
  GridOrderParams,
  LookupStepInfo,
  LookupStepType,
  WellsLookupStepSpec,
} from './LookupConfigModalConstants';

export interface WellsLookupStepConfigFormProps<T extends WellsLookupStepSpec | BoxContainersLookupStepSpec> {
  lookupInfo: LookupStepInfo & {lookupStep: T};
  onChange: (lookup: T, linkSchema: Schema | null) => void;
  disabled?: boolean;
  formErrors: FormErrors;
  allowAlternatingPatterns?: boolean;
  shouldShowNullForEmptyContentsTooltip?: boolean;
}

const baseTestAttrs = {component: 'WellsLookupStepConfigForm'};
export const DATA_TEST_ATTRS = {
  skipRows: {...baseTestAttrs, element: 'skipRows'},
  skipColumns: {...baseTestAttrs, element: 'skipColumns'},
  fillByQuadrant: {...baseTestAttrs, element: 'fillByQuadrant'},
  fillDirection: {...baseTestAttrs, element: 'fillDirection'},
  rowsToIgnore: {...baseTestAttrs, element: 'rowsToIgnore'},
  columnsToIgnore: {...baseTestAttrs, element: 'columnsToIgnore'},
  wellsToIgnore: {...baseTestAttrs, element: 'wellsToIgnore'},
  ignoreFilled: {...baseTestAttrs, element: 'ignoreFilled'},
  ignoreEmpty: {...baseTestAttrs, element: 'ignoreEmpty'},
};

// exported for test
export const getDisplayValue = (value: string): string => {
  value = value.replace('_', ' ').toLowerCase();
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

function FillDirectionSelector(props: {
  disabled?: boolean;
  fillDirection: FillDirection;
  onSelect: (value: FillDirection) => void;
}): JSX.Element {
  return (
    <SelectDropdown
      disabled={props.disabled}
      displayName={getDisplayValue(props.fillDirection)}
      menu={
        <Menu
          value={props.fillDirection}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.SMALL}
          scrollable={true}
        >
          <Option
            value={FillDirection.ACROSS_ROWS}
            contentType={OptionContentType.PLAIN}
            onSelect={() => {
              props.onSelect(FillDirection.ACROSS_ROWS);
            }}
          >
            Across rows
          </Option>
          <Option
            value={FillDirection.DOWN_COLUMNS}
            contentType={OptionContentType.PLAIN}
            onSelect={() => {
              props.onSelect(FillDirection.DOWN_COLUMNS);
            }}
          >
            Down columns
          </Option>
        </Menu>
      }
      {...getTestProps(DATA_TEST_ATTRS.fillDirection)}
    />
  );
}

function SkipRowsColsInput(props: {
  disabled?: boolean;
  value: number;
  placeholder: string;
  onChange: (value: number) => void;
  dataTestAttrs: DataTestAttrs;
}): JSX.Element {
  return (
    <NumericInput
      isDisabled={props.disabled}
      min={0}
      value={props.value}
      placeholder={props.placeholder}
      onChange={(e: NumericInputChangeEvent) => {
        if (e.target.numericValue != null) {
          props.onChange(e.target.numericValue);
        }
      }}
      validateInteger={true}
      {...getTestProps(props.dataTestAttrs)}
    />
  );
}

function getRowsColsToIgnoreFromInput(value: string): Array<number | string> {
  return value.split(',').map((val) => {
    const trimmedVal = val.trim();
    const parsedVal = parseInt(trimmedVal, 10);
    return Number.isNaN(parsedVal) ? trimmedVal : parsedVal;
  });
}

function onCSVInputKeyDown(
  e: ElementKeyboardEvent,
  currentValue: Array<number | string>,
  updateValueFn: (value: Array<number | string>) => void
): void {
  if (e.key === 'Backspace') {
    if (currentValue[currentValue.length - 1] === '') {
      e.preventDefault();
      updateValueFn(currentValue.slice(0, currentValue.length - 1));
    }
  }
}

function CSVInput(props: {
  disabled?: boolean;
  value: Array<string | number>;
  placeholder: string;
  onChange: (value: Array<string | number>) => void;
  parseCSVInputFn: (value: string) => Array<string | number>;
  hasError?: boolean;
  dataTestAttrs: DataTestAttrs;
}): JSX.Element {
  return (
    <TextInput
      disabled={props.disabled}
      value={props.value.join(', ')}
      placeholder={props.placeholder}
      onChange={(e: InputChangeEvent) => {
        props.onChange(props.parseCSVInputFn(e.target.value));
      }}
      onKeyDown={(e: ElementKeyboardEvent) => {
        onCSVInputKeyDown(e, props.value, (newValue: Array<number | string>) => {
          props.onChange(newValue);
        });
      }}
      hasError={props.hasError}
      {...getTestProps(props.dataTestAttrs)}
    />
  );
}

export default function WellsLookupStepConfigForm<
  T extends WellsLookupStepSpec | BoxContainersLookupStepSpec,
>(props: WellsLookupStepConfigFormProps<T>): JSX.Element {
  const lookupStep = props.lookupInfo.lookupStep;
  function updateOrder<T extends keyof GridOrderParams>(key: T, value: GridOrderParams[T]): void {
    props.onChange(
      {
        ...lookupStep,
        order: {...lookupStep.order, [key]: value},
      },
      props.lookupInfo.linkSchema
    );
  }

  function updateFilter<T extends keyof GridFilterParams>(key: T, value: GridFilterParams[T]): void {
    props.onChange(
      {
        ...lookupStep,
        filter: {...lookupStep.filter, [key]: value},
      },
      props.lookupInfo.linkSchema
    );
  }

  const containerName = lookupStep.type === LookupStepType.WELLS ? 'wells' : 'containers';

  return (
    <div>
      <Row>
        <Col md={4} className={css(styles.field)}>
          <FieldLabel label="Lookup direction" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <FillDirectionSelector
            disabled={props.disabled}
            fillDirection={lookupStep.order.fillDirection}
            onSelect={(value: FillDirection) => {
              updateOrder('fillDirection', value);
            }}
          />
        </Col>
        {props.allowAlternatingPatterns && (
          <>
            <Col md={4} className={css(styles.field)}>
              <FieldLabel label="Alternate rows" labelStyle={LabelStyle.LABEL} />
              <Tooltip
                title="Alternates rows to look up based on a positive integer.
Set to “0” if alternating patterns are not needed"
              >
                <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
              </Tooltip>
              <SkipRowsColsInput
                disabled={props.disabled}
                value={lookupStep.order.skipRows}
                placeholder="Alternate rows"
                onChange={(value: number) => {
                  updateOrder('skipRows', value);
                }}
                dataTestAttrs={DATA_TEST_ATTRS.skipRows}
              />
            </Col>
            <Col md={4} className={css(styles.field)}>
              <FieldLabel label="Alternate columns" labelStyle={LabelStyle.LABEL} />
              <Tooltip
                title="Alternates columns to look up based on a positive integer.
Set to “0” if alternating patterns are not needed"
              >
                <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
              </Tooltip>
              <SkipRowsColsInput
                disabled={props.disabled}
                value={lookupStep.order.skipColumns}
                placeholder="Alternate columns"
                onChange={(value: number) => {
                  updateOrder('skipColumns', value);
                }}
                dataTestAttrs={DATA_TEST_ATTRS.skipColumns}
              />
            </Col>
          </>
        )}
      </Row>
      <Row>
        <Col md={12} className={css(styles.field)}>
          <CheckboxInput
            className={css(styles.checkbox)}
            id={DATA_TEST_ATTRS.fillByQuadrant.element}
            disabled={props.disabled}
            checked={lookupStep.order.fillByQuadrant}
            label="Look up by quadrant"
            onChange={(e) => {
              updateOrder('fillByQuadrant', e.target.checked);
            }}
            {...getTestProps(DATA_TEST_ATTRS.fillByQuadrant)}
          />
          <Tooltip title="If checked, returns wells in quadrant order (Q1 > Q2 > Q3 > Q4)">
            <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
          </Tooltip>
        </Col>
      </Row>
      <Row>
        <Col md={4} className={css(styles.field)}>
          <FieldLabel label="Rows to ignore" labelStyle={LabelStyle.LABEL} />
          <Tooltip title="Use comma-separated integers to designate which rows to ignore in the lookup">
            <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
          </Tooltip>
          <CSVInput
            disabled={props.disabled}
            value={lookupStep.filter.rowsToIgnore}
            placeholder="Rows to ignore"
            parseCSVInputFn={getRowsColsToIgnoreFromInput}
            onChange={(value: Array<string | number>) => {
              updateFilter('rowsToIgnore', value);
            }}
            hasError={props.formErrors ? props.formErrors.rowsToIgnore : false}
            dataTestAttrs={DATA_TEST_ATTRS.rowsToIgnore}
          />
          {props.formErrors && props.formErrors.rowsToIgnore ? (
            <ErrorMessage error="Rows to ignore must be unique comma-separated integers" />
          ) : null}
        </Col>
        <Col md={4} className={css(styles.field)}>
          <FieldLabel label="Columns to ignore" labelStyle={LabelStyle.LABEL} />
          <Tooltip title="Use comma-separated integers to designate which columns to ignore in the lookup">
            <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
          </Tooltip>
          <CSVInput
            disabled={props.disabled}
            value={lookupStep.filter.columnsToIgnore}
            placeholder="Columns to ignore"
            parseCSVInputFn={getRowsColsToIgnoreFromInput}
            onChange={(value: Array<string | number>) => {
              updateFilter('columnsToIgnore', value);
            }}
            hasError={props.formErrors ? props.formErrors.columnsToIgnore : false}
            dataTestAttrs={DATA_TEST_ATTRS.columnsToIgnore}
          />
          {props.formErrors && props.formErrors.columnsToIgnore ? (
            <ErrorMessage error="Columns to ignore must be unique comma-separated integers" />
          ) : null}
        </Col>
        <Col md={4} className={css(styles.field)}>
          <FieldLabel label={`${capitalize(containerName)} to ignore`} labelStyle={LabelStyle.LABEL} />
          <Tooltip
            title={`Use comma-separated ${containerName} (e.g. "A6" or "6") to designate which ${containerName} to ignore in the lookup`}
          >
            <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
          </Tooltip>
          <CSVInput
            disabled={props.disabled}
            value={lookupStep.filter.wellsToIgnore}
            placeholder={`${capitalize(containerName)} to ignore`}
            parseCSVInputFn={(value: string) => value.split(',').map((val) => val.trim())}
            onChange={(value: Array<string>) => {
              updateFilter('wellsToIgnore', value);
            }}
            hasError={props.formErrors ? props.formErrors.wellsToIgnore : false}
            dataTestAttrs={DATA_TEST_ATTRS.wellsToIgnore}
          />
          {props.formErrors && props.formErrors.wellsToIgnore ? (
            <ErrorMessage
              error={`${capitalize(
                containerName
              )} to ignore must be unique comma-separated values of the form "A6" or "6"`}
            />
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col md={4} className={css(styles.field)}>
          <Tooltip
            title="Cannot ignore both empty and filled wells"
            disabled={!lookupStep.filter.ignoreFilled}
          >
            <CheckboxInput
              className={css(styles.checkbox)}
              id={DATA_TEST_ATTRS.ignoreEmpty.element}
              disabled={props.disabled || lookupStep.filter.ignoreFilled}
              checked={lookupStep.filter.ignoreEmpty}
              label="Ignore empty wells"
              onChange={(e) => {
                updateFilter('ignoreEmpty', e.target.checked);
              }}
              {...getTestProps(DATA_TEST_ATTRS.ignoreEmpty)}
            />
          </Tooltip>
          {props.shouldShowNullForEmptyContentsTooltip && (
            <Tooltip title="If checked, ignores wells without any contents; if left unchecked, any empty wells will lead to empty rows in the new column, which can be removed by a Filter transform if desired">
              <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
            </Tooltip>
          )}
        </Col>
        <Col md={4} className={css(styles.field)}>
          <Tooltip
            title="Cannot ignore both empty and filled wells"
            disabled={!lookupStep.filter.ignoreEmpty}
          >
            <CheckboxInput
              id={DATA_TEST_ATTRS.ignoreFilled.element}
              disabled={props.disabled || lookupStep.filter.ignoreEmpty}
              checked={lookupStep.filter.ignoreFilled}
              label="Ignore filled wells"
              onChange={(e) => {
                updateFilter('ignoreFilled', e.target.checked);
              }}
              {...getTestProps(DATA_TEST_ATTRS.ignoreFilled)}
            />
          </Tooltip>
        </Col>
      </Row>
    </div>
  );
}

const styles = StyleSheet.create({
  checkbox: {
    display: 'inline-block',
  },
  field: {
    marginBottom: '8px',
  },
  infoIcon: {
    marginLeft: '4px',
    color: GRAY_DARK,
  },
});
